import { useState, useEffect } from 'react';
import { recompileResume, getResumeSource } from '../services/resumeMatchingFunctions';

interface GeneratedResumePreviewProps {
  applicationId: string;
  generatedResumeId: string;
  downloadUrl: string;
  typstDownloadUrl?: string;
  retryReason?: string;
  warnings?: string[];
  onRegenerate: () => void;
  onClose?: () => void;
  autoEdit?: boolean;
}

export default function GeneratedResumePreview({
  applicationId,
  generatedResumeId,
  downloadUrl,
  typstDownloadUrl,
  retryReason,
  warnings,
  onRegenerate,
  onClose,
  autoEdit,
}: GeneratedResumePreviewProps) {
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [typstCode, setTypstCode] = useState<string | null>(null);
  const [isCompiling, setIsCompiling] = useState(false);
  const [currentDownloadUrl, setCurrentDownloadUrl] = useState(downloadUrl);
  const [currentTypstUrl, setCurrentTypstUrl] = useState(typstDownloadUrl);
  const [localWarnings, setLocalWarnings] = useState<string[]>(warnings || []);

  // Use a key to force iframe reload
  const [iframeKey, setIframeKey] = useState(0);

  const handleEditClick = async () => {
    if (isEditing) {
      setIsEditing(false);
      return;
    }

    if (!typstCode) {
      setIsFetching(true);
      setFetchError(null);
      try {
        const result = await getResumeSource({
          applicationId,
          generatedResumeId,
        });
        setTypstCode(result.typstSource);
      } catch (err: any) {
        setFetchError(`Failed to load source: ${err.message || 'Unknown error'}`);
        console.error('Error fetching typst source:', err);
      } finally {
        setIsFetching(false);
      }
    }
    setIsEditing(true);
  };

  const handleRecompile = async () => {
    if (!typstCode) return;
    setIsCompiling(true);
    try {
      const result = await recompileResume({
        applicationId,
        generatedResumeId,
        typstSource: typstCode,
      });

      setCurrentDownloadUrl(result.downloadUrl);
      if (result.typstDownloadUrl) {
        setCurrentTypstUrl(result.typstDownloadUrl);
      }
      if (result.warnings) {
        setLocalWarnings(result.warnings);
      }
      
      // Force iframe reload
      setIframeLoaded(false);
      setIframeKey(prev => prev + 1);
      setIsEditing(false);
    } catch (err: any) {
      alert(`Recompile failed: ${err.message || 'Unknown error'}`);
    } finally {
      setIsCompiling(false);
    }
  };

  useEffect(() => {
    if (autoEdit) {
      handleEditClick();
    }
  }, [autoEdit]);

  return (
    <div className="flex flex-col gap-4">
      {/* Retry / warnings banners */}
      {retryReason && (
        <div className="flex items-start gap-2 px-3 py-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg text-xs text-yellow-800 dark:text-yellow-300">
          <svg className="w-4 h-4 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M12 3a9 9 0 100 18A9 9 0 0012 3z" />
          </svg>
          <span>
            <span className="font-medium">Auto-corrected:</span> {retryReason}
          </span>
        </div>
      )}

      {localWarnings && localWarnings.length > 0 && (
        <div className="flex flex-col gap-1 px-3 py-2 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-700 rounded-lg text-xs text-orange-800 dark:text-orange-300">
          <p className="font-medium">Minor issues (not blocking):</p>
          <ul className="list-disc list-inside space-y-0.5">
            {localWarnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Action buttons */}
      <div className="flex flex-wrap gap-2">
        <a
          href={currentDownloadUrl}
          target="_blank"
          rel="noopener noreferrer"
          download
          className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
          Download PDF
        </a>

        {currentTypstUrl && (
          <button
            onClick={handleEditClick}
            disabled={isFetching}
            className={`inline-flex items-center gap-2 px-4 py-2 text-sm border font-medium rounded-lg transition-colors ${
              isEditing 
                ? 'bg-indigo-50 border-indigo-300 text-indigo-700 dark:bg-indigo-900/40 dark:border-indigo-500 dark:text-indigo-200' 
                : 'border-indigo-300 dark:border-indigo-600 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/30'
            }`}
          >
            {isFetching ? (
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            ) : (
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            )}
            {isEditing ? 'Hide Source' : 'Edit Source'}
          </button>
        )}

        <button
          onClick={onRegenerate}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Regenerate
        </button>

        {onClose && (
          <button
            onClick={onClose}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
          >
            Close
          </button>
        )}
      </div>

      {/* Editor or Preview */}
      <div className="flex flex-col gap-4">
        {isEditing && (
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Typst Source Code</label>
              <div className="flex gap-2">
                <button
                  onClick={() => setIsEditing(false)}
                  className="px-3 py-1 text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRecompile}
                  disabled={isCompiling}
                  className="inline-flex items-center gap-2 px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 transition-colors disabled:opacity-50"
                >
                  {isCompiling ? (
                    <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                  ) : (
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                  {isCompiling ? 'Compiling...' : 'Apply & Recompile'}
                </button>
              </div>
            </div>
            <textarea
              value={typstCode || ''}
              onChange={(e) => setTypstCode(e.target.value)}
              className="w-full h-[500px] p-4 font-mono text-xs bg-gray-900 text-gray-100 rounded-lg border border-gray-700 focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
              spellCheck={false}
              placeholder="Typst code is loading..."
            />
          </div>
        )}

        {/* PDF iframe preview */}
        <div className={`relative w-full ${isEditing ? 'hidden' : 'block'}`} style={{ height: '800px' }}>
          {/* Loading overlay — shown until iframe fires onLoad */}
          {!iframeLoaded && (
            isFetching ? (
              <div className="flex flex-col items-center justify-center py-12 bg-gray-900 rounded-lg border border-gray-700">
                <svg className="animate-spin h-8 w-8 text-blue-500 mb-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                <div className="text-sm text-gray-400 italic">Fetching Typst source...</div>
              </div>
            ) : fetchError ? (
              <div className="p-6 bg-red-900/20 border border-red-900/50 rounded-lg text-center">
                <p className="text-red-400 text-sm mb-4">{fetchError}</p>
                <button 
                  onClick={() => { setFetchError(null); handleEditClick(); }}
                  className="px-4 py-2 bg-red-800 hover:bg-red-700 text-white text-xs rounded transition-colors"
                >
                  Try Again
                </button>
              </div>
            ) : (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 z-10">
                <svg className="animate-spin h-8 w-8 text-indigo-500 mb-3" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                <p className="text-sm text-gray-500 dark:text-gray-400">Loading PDF preview...</p>
              </div>
            )
          )}

          <iframe
            key={iframeKey}
            src={currentDownloadUrl}
            title="Generated Resume Preview"
            className="w-full h-full rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm"
            onLoad={() => setIframeLoaded(true)}
            style={{ display: iframeLoaded ? 'block' : 'none' }}
          />
        </div>
      </div>

      <p className="text-xs text-gray-400 dark:text-gray-500">
        Preview uses a signed URL that expires in 24 hours. Download the PDF to keep a permanent copy.
      </p>
    </div>
  );
}
